import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Key, Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { authConfig } from '../../config/auth';
import LandingNav from '../LandingNav';
import '../../styles/auth.css';
import '../../styles/landing.css';

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = (location.state as { email: string })?.email || '';

  const [email, setEmail] = useState(emailFromState);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${authConfig.apiUrl}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to reset password');
        setLoading(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <LandingNav />

      {/* Auth Content */}
      <div className="auth-container">
        <div className="auth-header">
          <p className="auth-header-subtitle">Create New Password</p>
        </div>

        <div className="auth-card">
          <h2 className="auth-card-title">Reset Password</h2>
          <p className="text-center info-text" style={{ marginBottom: '24px' }}>
            Enter the code from your email and your new password
          </p>

          {success ? (
            <div className="success-message">
              <div className="success-icon">
                <CheckCircle size={32} color="#00bf62" />
              </div>
              <div className="success-text">
                Password reset successfully! Redirecting to login...
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="auth-form">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="input-container">
                <span className="input-icon">
                  <Mail size={18} />
                </span>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                  className="input-field"
                />
              </div>

              <div className="input-container">
                <span className="input-icon">
                  <Key size={18} />
                </span>
                <input
                  id="code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  disabled={loading}
                  className="input-field"
                />
              </div>

              <div className="input-container">
                <span className="input-icon">
                  <Lock size={18} />
                </span>
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  disabled={loading}
                  className="input-field"
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="toggle-password-btn"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <div className="hint-text">
                Password must be at least 8 characters with uppercase, lowercase, and numbers
              </div>

              <button
                type="submit"
                disabled={loading}
                className="auth-button"
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>

              <div className="text-center mt-sm">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="auth-link"
                >
                  ← Back to Login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
